import { action, computed, makeObservable } from "mobx";
import { AppSubStoreArgs } from "@/store/types";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppCalloutKind, SpaceAccountAppCalloutModelData } from "@/store/app-callout/types";
import { SpaceAccountAppCalloutObservable } from "@/store/app-callout/SpaceAccountAppCalloutObservable";
import { sortBy } from "lodash-es";
import { DismissSpaceAccountAppCalloutOperation } from "@/store/sync/operations/app-callout/DismissSpaceAccountAppCalloutOperation";
import { DateTime } from "luxon";
import { Maybe } from "@/domains/common/types";
import { PlatformIconKind } from "@/domains/api/types";

export class AppStoreSpaceAccountAppCalloutsStore extends BaseSyncModelStore<
  SpaceAccountAppCalloutObservable,
  SpaceAccountAppCalloutModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountAppCallout, ...injectedDeps });

    makeObservable(this, {
      pastChatsBanner: true,
      getAppCalloutBanner: true,
      createSyncModel: false,
      appCalloutBanners: computed,
      appCalloutModals: computed,
      highestPriorityVisibleBanner: computed,
      highestPriorityVisibleModal: computed,
      dismissAppCallout: action,
    });
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountAppCalloutModelData>
  ): SpaceAccountAppCalloutObservable {
    return new SpaceAccountAppCalloutObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  get appCalloutBanners(): SpaceAccountAppCalloutObservable[] {
    return this.all.filter(
      appCallout =>
        appCallout.calloutKind === AppCalloutKind.BANNER &&
        /**
         * Filtering out Legacy Icon Kinds
         * Only allow new kinds of nullable.
         */
        appCallout.iconKind !== PlatformIconKind.Chat &&
        appCallout.iconKind !== PlatformIconKind.Chrome &&
        appCallout.iconKind !== PlatformIconKind.DownloadLaptopMobile &&
        appCallout.iconKind !== PlatformIconKind.Laptop &&
        appCallout.iconKind !== PlatformIconKind.Mobile
    );
  }

  get appCalloutModals(): SpaceAccountAppCalloutObservable[] {
    return this.all.filter(appCallout => appCallout.calloutKind === AppCalloutKind.MODAL);
  }

  /**
   * For now, the highest-priority app banner is just the one with the
   * earliest created-at date.
   */
  get highestPriorityVisibleBanner(): SpaceAccountAppCalloutObservable | null {
    const visibleBanners = this.appCalloutBanners.filter(appCallout => appCallout.isVisible);

    return sortBy(visibleBanners, "createdAt")?.[0] ?? null;
  }

  get highestPriorityVisibleModal(): Maybe<SpaceAccountAppCalloutObservable> {
    const visibleModals = this.appCalloutModals.filter(appCallout => appCallout.isVisible);

    return sortBy(visibleModals, "createdAt")?.[0];
  }

  getAppCalloutBanner(id: string): SpaceAccountAppCalloutObservable | undefined {
    return this.appCalloutBanners.find(callout => callout.id === id);
  }

  get pastChatsBanner(): SpaceAccountAppCalloutObservable | undefined {
    return this.appCalloutBanners.find(
      callout => callout.appCalloutId == "b1e1a6a4-d86b-4468-84a5-ae34fa1f709f"
    );
  }

  /**
   * Actions
   */

  async dismissAppCallout({ appCallout }: { appCallout: SpaceAccountAppCalloutObservable }) {
    await new DismissSpaceAccountAppCalloutOperation({
      store: this.store,
      payload: {
        space_account_app_callout_id: appCallout.id,
        dismissed_at: DateTime.now().toISO(),
      },
    }).execute();
  }
}
